import { gql } from "@apollo/client";

export const fields = gql`
  fragment StudentLoginStepFields on StudentLoginStep {
    version
    authorizationUrl
    authorizationExpiresAt
    signupReady
    activationReady
    recoveryReady
    flowToken
    action
    setupKind
    maskedMobile
    expiresAt
    retryAfterSeconds
    passwordMinLength
    passwordMaxBytes
    mobileVerifedToken
    enrollmentToken
    supportCode
    returnTo
    authenticationMethod
    profile {
      fname
      lname
      date_of_birth
    }
    auth {
      accessToken
      roles
      userAccount {
        _id
        fname
        lname
        mobile
        smartmatrix_id
        smartmatrix_identity_status
        role
        status
      }
    }
  }
`;
const operations: Record<string, string> = {
  beginStudentLogin: "BeginStudentLoginInput!",
  sendStudentLoginOtp: "StudentFlowInput!",
  verifyStudentLoginOtp: "StudentFlowOtpInput!",
  verifyStudentLocalOwnership: "StudentLocalOwnershipInput!",
  completeStudentSmartMatrixSetup: "CompleteStudentSetupInput!",
  completeStudentEnrollment: "CompleteStudentEnrollmentInput!",
  completeStudentAccountLink: "StudentFlowProofInput!",
  completeStudentRedirect: "CompleteStudentRedirectInput!",
};
export const documents = Object.fromEntries(
  Object.entries(operations).map(([name, type]) => [
    name,
    gql`mutation ${name}($input: ${type}) { ${name}(input: $input) { ...StudentLoginStepFields } } ${fields}`,
  ]),
);
export const createMutation = gql`
  mutation MobileCreateStudent($input: UserAccountIn) {
    createStudent(input: $input) {
      accessToken
      roles
      authenticationMethod
      returnTo
      userAccount {
        _id
        fname
        lname
        mobile
        smartmatrix_id
        smartmatrix_identity_status
        role
        status
      }
    }
  }
`;
export const logoutMutation = gql`
  mutation LogoutStudent {
    logoutStudent {
      localRevoked
      upstreamRevoked
      logoutUrl
    }
  }
`;
export type Step = {
  version?: number;
  authorizationUrl?: string;
  authorizationExpiresAt?: string;
  signupReady?: boolean;
  activationReady?: boolean;
  recoveryReady?: boolean;
  action: string;
  setupKind?: string;
  flowToken?: string;
  expiresAt?: string;
  retryAfterSeconds?: number;
  maskedMobile?: string;
  mobileVerifedToken?: string;
  enrollmentToken?: string;
  passwordMinLength?: number;
  passwordMaxBytes?: number;
  supportCode?: string;
  returnTo?: string;
  authenticationMethod?: string;
  profile?: { fname?: string; lname?: string; date_of_birth?: string };
  auth?: {
    accessToken: string;
    userAccount?: { _id?: string; smartmatrix_id?: string };
  };
};
export type Flow = {
  flowToken: string;
  browserVerifier: string;
  expiresAt: string;
};
export const districts = [
  "Ampara",
  "Anuradhapura",
  "Badulla",
  "Batticaloa",
  "Colombo",
  "Galle",
  "Gampaha",
  "Hambantota",
  "Jaffna",
  "Kalutara",
  "Kandy",
  "Kegalle",
  "Kilinochchi",
  "Kurunegala",
  "Mannar",
  "Matale",
  "Matara",
  "Moneragala",
  "Mullaitivu",
  "Nuwara Eliya",
  "Polonnaruwa",
  "Puttalam",
  "Ratnapura",
  "Trincomalee",
  "Vavuniya",
];
export function safeDestination(value?: string) {
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    Array.from(value).some((character) => character.charCodeAt(0) <= 32)
  )
    return "/profile";
  return value;
}
export function navigationUrl(value: string, origin: string, path: string) {
  const url = new URL(value);
  if (
    url.origin !== new URL(origin).origin ||
    url.pathname !== path ||
    url.username ||
    url.password ||
    url.hash
  )
    throw new Error("Invalid navigation destination");
  return url.href;
}
const encode = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
export async function newBinding() {
  const browserVerifier = encode(crypto.getRandomValues(new Uint8Array(32)));
  return {
    browserVerifier,
    browserChallenge: encode(
      new Uint8Array(
        await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(browserVerifier),
        ),
      ),
    ),
  };
}
export function readFlow(key: string): Flow | null {
  try {
    const flow = JSON.parse(sessionStorage.getItem(key) || "null");
    return flow &&
      typeof flow.flowToken === "string" &&
      typeof flow.browserVerifier === "string" &&
      Date.parse(flow.expiresAt) > Date.now()
      ? flow
      : null;
  } catch {
    return null;
  }
}
export function errorCode(error: any): string {
  const value =
    error?.graphQLErrors?.[0]?.extensions?.code ||
    error?.errors?.[0]?.extensions?.code;
  return typeof value === "string" &&
    /^(LOGIN|SIGNUP|CREATE)_[A-Z_]+$/.test(value)
    ? value
    : "";
}
export const errorMessages: Record<string, string> = {
  LOGIN_DETAILS_INVALID:
    "Check your mobile number and sign-in details, then try again.",
  LOGIN_FLOW_EXPIRED: "This sign-in has expired. Start again.",
  LOGIN_FLOW_INVALID: "This sign-in is no longer valid. Start again.",
  LOGIN_RATE_LIMITED: "Please wait before trying again.",
  LOGIN_OTP_INVALID: "That verification code is incorrect. Try again.",
  LOGIN_OTP_EXPIRED: "Your code has expired. Start again to verify your phone.",
  LOGIN_CREDENTIALS_INVALID:
    "The old institute password could not be verified.",
  LOGIN_SIGN_IN_AGAIN:
    "Continue with a fresh SmartMatrix sign-in. Your setup may already be saved.",
  SIGNUP_ALREADY_COMPLETED:
    "Your registration is already saved. Start again to sign in.",
  SIGNUP_VERIFICATION_RETRY:
    "Your registration may be saved. Start again to sign in.",
  LOGIN_SUBJECT_MISMATCH:
    "The SmartMatrix account does not match this sign-in. Start again or contact your institute.",
  LOGIN_REVIEW_REQUIRED:
    "Your account needs institute support review. No accounts have been merged.",
  LOGIN_CANCELLED: "Sign-in was cancelled. You can start again.",
  LOGIN_DEVICE_NOT_APPROVED:
    "This device is not approved for your institute account. Contact your institute to request device approval, then sign in again.",
  LOGIN_DEVICE_REQUIRED:
    "This device could not be verified. Contact your institute.",
  LOGIN_PROVIDER_UNAVAILABLE:
    "SmartMatrix is temporarily unavailable. Please try again later.",
  LOGIN_CAPABILITY_UNAVAILABLE:
    "Student sign-in is temporarily unavailable. Please try again later.",
  LOGIN_LOCAL_SAVE_FAILED:
    "Setup could not be confirmed. Start again to sign in or contact support; do not repeat account creation.",
};

export function responseError(errors: readonly unknown[] | undefined) {
  return Object.assign(new Error("Sign-in could not be confirmed"), {
    graphQLErrors: errors || [],
  });
}
